/** A calendar day as `YYYY-MM-DD`. */
export type DayKey = string

export interface TimeZoneOption {
  id: string
  label: string
  /** "PT": what a time in this zone is written with. */
  short?: string
}

/** The US zones, west to east, plus the machine's own when it's elsewhere. */
const US_ZONES: Array<TimeZoneOption> = [
  { id: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', short: 'HT' },
  { id: 'America/Anchorage', label: 'Alaska Time (AKT)', short: 'AKT' },
  { id: 'America/Los_Angeles', label: 'Pacific Time (PT)', short: 'PT' },
  { id: 'America/Denver', label: 'Mountain Time (MT)', short: 'MT' },
  { id: 'America/Chicago', label: 'Central Time (CT)', short: 'CT' },
  { id: 'America/New_York', label: 'Eastern Time (ET)', short: 'ET' },
]

export const detectedTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

export function timeZoneOptions(detected: string): Array<TimeZoneOption> {
  if (US_ZONES.some((z) => z.id === detected)) return US_ZONES
  return [{ id: detected, label: zoneCity(detected) }, ...US_ZONES]
}

const zoneCity = (zone: string) => (zone.split('/').at(-1) ?? zone).replace(/_/g, ' ')

export const timeZoneLabel = (zone: string) => US_ZONES.find((z) => z.id === zone)?.label ?? zoneCity(zone)

/** "UTC−7", the zone's offset at `at`. */
export function utcOffset(zone: string, at = new Date()): string {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')
  return (part?.value ?? '').replace('GMT', 'UTC').replace('-', '−')
}

const zoneShort = (zone: string, at: Date) =>
  US_ZONES.find((z) => z.id === zone)?.short ??
  new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(at)
    .find((p) => p.type === 'timeZoneName')?.value

export const dayKeyOf = (instant: string | Date, zone: string): DayKey =>
  new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(instant),
  )

/** A day key's own date, at noon UTC, so its weekday and number read the same in every zone. */
const dayDate = (key: DayKey) => new Date(`${key}T12:00:00Z`)

const shiftDay = (key: DayKey, days: number): DayKey =>
  new Date(dayDate(key).getTime() + days * 86_400_000).toISOString().slice(0, 10)

/** Monday to Friday of the week `from` falls in, in `zone`, and of the week after. */
export function bookingWeeks(from: string, zone: string): Array<Array<DayKey>> {
  const first = dayKeyOf(from, zone)
  const monday = shiftDay(first, -((dayDate(first).getUTCDay() + 6) % 7))
  return [0, 1].map((week) => [0, 1, 2, 3, 4].map((day) => shiftDay(monday, week * 7 + day)))
}

export function slotsByDay(slots: Array<string>, zone: string): Map<DayKey, Array<string>> {
  const days = new Map<DayKey, Array<string>>()
  for (const slot of slots) {
    const key = dayKeyOf(slot, zone)
    days.set(key, [...(days.get(key) ?? []), slot])
  }
  return days
}

export const formatDay = (key: DayKey, style: 'short' | 'long' = 'short') =>
  dayDate(key).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: style })

export const formatDayOfMonth = (key: DayKey) => dayDate(key).getUTCDate()

export const formatMonth = (key: DayKey) =>
  dayDate(key).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' })

export const formatTime = (instant: string, zone: string) =>
  new Date(instant).toLocaleTimeString('en-US', { timeZone: zone, hour: 'numeric', minute: '2-digit' })

/** "Mon, Oct 5": the day the times under it are on. */
export const formatDayHeading = (key: DayKey) =>
  dayDate(key).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })

/** "Mon, Oct 5 at 1:30 PM PT": one string for the summary, the confirmation and its card. */
export function formatSlot(instant: string, zone: string, separator = ' at '): string {
  const at = new Date(instant)
  const date = at.toLocaleDateString('en-US', { timeZone: zone, weekday: 'short', month: 'short', day: 'numeric' })
  return `${date}${separator}${formatTime(instant, zone)} ${zoneShort(zone, at)}`
}
